export interface HyphenationPattern {
  pattern: string;
  exceptions?: string[];
}

export interface HyphenationResult {
  word: string;
  breaks: number[];
}

const ENGLISH_PATTERNS: string[] = [
  'a1',
  'a2',
  'a3',
  'a4',
  'a5',
  'a6',
  'a7',
  'a8',
  'a9',
  'b1',
  'b2',
  'b3',
  'b4',
  'c1',
  'c2',
  'c3',
  'd1',
  'd2',
  'd3',
  'd4',
  'e1',
  'e2',
  'e3',
  'e4',
  'e5',
  'f1',
  'f2',
  'f3',
  'f4',
  'g1',
  'g2',
  'g3',
  'g4',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'i1',
  'i2',
  'i3',
  'i4',
  'i5',
  'j1',
  'k1',
  'k2',
  'k3',
  'l1',
  'l2',
  'l3',
  'l4',
  'l5',
  'm1',
  'm2',
  'm3',
  'm4',
  'm5',
  'n1',
  'n2',
  'n3',
  'n4',
  'n5',
  'o1',
  'o2',
  'o3',
  'o4',
  'o5',
  'o6',
  'p1',
  'p2',
  'p3',
  'p4',
  'q1',
  'r1',
  'r2',
  'r3',
  'r4',
  's1',
  's2',
  's3',
  's4',
  's5',
  't1',
  't2',
  't3',
  't4',
  't5',
  'u1',
  'u2',
  'u3',
  'v1',
  'v2',
  'v3',
  'w1',
  'w2',
  'w3',
  'w4',
  'x1',
  'y1',
  'y2',
  'y3',
  'z1',
  'z2',
  'z3',
  'th1',
  'th2',
  'th3',
  'ch1',
  'sh1',
  'ing3',
  'tion2',
  'tion3',
  'sion2',
  'ment2',
  'ment3',
  'ness2',
  'ness3',
  'able2',
  'ible2',
  'ful2',
  'less2',
  'ness2',
  'able3',
];

export class Hyphenator {
  private patterns: Map<string, [number, number][]>;
  private exceptions: Set<string>;
  private language: string;

  constructor(language = 'en', patterns?: string[], exceptions?: string[]) {
    this.language = language;
    this.patterns = new Map();
    this.exceptions = new Set();

    if (patterns) {
      this.loadPatterns(patterns);
    } else {
      this.loadDefaultPatterns();
    }

    if (exceptions) {
      this.loadExceptions(exceptions);
    }
  }

  private loadDefaultPatterns(): void {
    this.loadPatterns(ENGLISH_PATTERNS);
  }

  private loadPatterns(patterns: string[]): void {
    for (const pattern of patterns) {
      const cleanedPattern = pattern.replace(/\d/g, '').toLowerCase();
      const entries: [number, number][] = [];

      let i = 0;
      for (const char of pattern) {
        if (/\d/.test(char)) {
          entries.push([i, parseInt(char, 10)]);
        } else if (/\p{L}/u.test(char)) {
          i++;
        }
      }

      this.patterns.set(cleanedPattern, entries);
    }
  }

  private loadExceptions(exceptions: string[]): void {
    for (const word of exceptions) {
      this.exceptions.add(word.toLowerCase());
    }
  }

  hyphenate(word: string): string[] {
    const cleanWord = word.replace(/[^\p{L}]/gu, '');
    const lowerWord = cleanWord.toLowerCase();

    if (lowerWord.length < 2) {
      return [word];
    }

    if (this.exceptions.has(lowerWord)) {
      return this.hyphenateException(word);
    }

    const result = this.computeHyphenationPoints(lowerWord);
    return this.applyHyphenation(word, result);
  }

  private hyphenateException(word: string): string[] {
    const parts: string[] = [];
    let current = '';

    for (const char of word) {
      if (char === '-') {
        if (current) {
          parts.push(current);
          current = '';
        }
      } else {
        current += char;
      }
    }

    if (current) {
      parts.push(current);
    }

    return parts.length > 0 ? parts : [word];
  }

  private computeHyphenationPoints(word: string): number[] {
    const points: number[] = new Array(word.length + 1).fill(0);

    const paddedWord = `.${  word  }.`;

    for (let i = 0; i < paddedWord.length; i++) {
      for (let j = i + 1; j <= paddedWord.length; j++) {
        const substring = paddedWord.slice(i, j);

        if (this.patterns.has(substring)) {
          const entries = this.patterns.get(substring)!;

          for (const [pos, val] of entries) {
            const idx = i + pos;
            if (idx < points.length) {
              points[idx] = Math.max(points[idx], val);
            }
          }
        }
      }
    }

    const result: number[] = [];
    for (let i = 1; i < points.length - 1; i++) {
      if (points[i] % 2 === 1) {
        result.push(i);
      }
    }

    return result;
  }

  private applyHyphenation(word: string, points: number[]): string[] {
    if (points.length === 0) {
      return [word];
    }

    const parts: string[] = [];
    let lastIndex = 0;

    for (const point of points) {
      if (point > 0 && point < word.length - 1) {
        parts.push(word.slice(lastIndex, point));
        lastIndex = point;
      }
    }

    parts.push(word.slice(lastIndex));

    return parts.filter((p) => p.length > 0);
  }

  getLanguage(): string {
    return this.language;
  }
}

const defaultHyphenator = new Hyphenator('en');

export function hyphenate(word: string): string[] {
  return defaultHyphenator.hyphenate(word);
}

export function createHyphenator(
  language: string,
  patterns?: string[],
  exceptions?: string[]
): Hyphenator {
  return new Hyphenator(language, patterns, exceptions);
}

export function addHyphenationPoints(
  text: string,
  hyphenator: Hyphenator = defaultHyphenator
): string {
  const words = text.split(/(\s+)/);

  return words
    .map((word) => {
      if (word.length === 0 || /^\s+$/.test(word)) {
        return word;
      }

      const parts = hyphenator.hyphenate(word);

      if (parts.length > 1) {
        return parts.join('-');
      }

      return word;
    })
    .join('');
}
